import { RequestKind, boolean, string } from '@sb/library/base';
import { AttendanceAttachmentDisplayItem, AttendanceAttachmentFileInputItem, AttendanceAttachmentInputItem, AttendanceConflictCheckSearchOptions, AttendanceDisplayItem, AttendanceHistorySearchOptions, AttendanceInputItem, AttendanceMakeupCreditIssueDisplayItem, AttendanceMakeupCreditIssueSearchOptions, AttendanceOptionsDisplayItem, AttendanceOptionsSummaryDisplayItem, AttendanceOptionsSummarySearchOptions, AttendancePaymentInputItem, AttendancePaymentUpdateItem, AttendanceSearchOptions, AttendanceSummaryDisplayItem, AttendanceSummarySearchOptions, AttendanceUpdateItem, BatchAttendanceUpdateItems, CalendarBookingInputItem, CancelAttendanceInputItem, CreateAttendanceResultItem, DisplayItemCollection, GroupNoteUpdateItem, LibraryOnlineDisplayItem, OnlineResourceSpaceDisplayItem, OnlineResourcesSearchOptions, PracticeLogDisplayItem, PracticeLogSearchOptions, SendAttendanceNotesBulkInputItem, SendAttendanceNotesEmailInputItem, StudentCalendarConflictDisplayItem, StudentDisplayItem, TransactionDisplayItem } from '@sb/library/data-models';
import { ApiRoute } from '../api-route';

export namespace AttendanceRoutes {
    export const Get = ApiRoute.Define({
        URL: (attendanceID: string) => `attendance/${attendanceID}`,
        Kind: RequestKind.Get,
        ResultType: AttendanceDisplayItem
    });

    export const GetFromEvent = ApiRoute.Define({
        URL: (eventID: string, attendanceID: string) => `events/${eventID}/attendance/${attendanceID}`,
        Kind: RequestKind.Get,
        ResultType: AttendanceDisplayItem
    });

    export const GetAll = ApiRoute.Define({
        URL: `attendance`,
        Kind: RequestKind.Get,
        ResultType: DisplayItemCollection<AttendanceDisplayItem>
    });

    export const GetAllFromEvent = ApiRoute.Define({
        URL: (eventID: string) => `events/${eventID}/attendance`,
        Kind: RequestKind.Get,
        ResultType: DisplayItemCollection<AttendanceDisplayItem>
    });

    export const GetStudents = ApiRoute.Define({
        URL: (eventID: string, attendanceID: string) => `events/${eventID}/attendance/${attendanceID}/student`,
        Kind: RequestKind.Get,
        ResultType: StudentDisplayItem
    });

    export const GetSummaries = ApiRoute.Define({
        URL: 'summary/attendance',
        Kind: RequestKind.Post,
        BodyType: AttendanceSummarySearchOptions,
        ResultType: DisplayItemCollection<AttendanceSummaryDisplayItem>
    });

    export const Add = ApiRoute.Define({
        URL: (eventID: string) => `events/${eventID}/attendance`,
        Kind: RequestKind.Post,
        BodyType: AttendanceInputItem,
        ResultType: Array<CreateAttendanceResultItem>
    });

    export const CreateFileForSubstitute = ApiRoute.Define({
        URL: (eventID: string) => `event/${eventID}/onlineresources`,
        Kind: RequestKind.Post,
        BodyType: FormData,
        ResultType: string
    });

    export const SearchByEventID = ApiRoute.Define({
        URL: (eventID: string) => `search/event/${eventID}/onlineresources`,
        Kind: RequestKind.Post,
        BodyType: OnlineResourcesSearchOptions,
        ResultType: DisplayItemCollection<LibraryOnlineDisplayItem>,
        PagingAllowed: true
    });

    export const GetSpacesByEventID = ApiRoute.Define({
        URL: (eventID: string) => `event/${eventID}/onlineresources/spaces`,
        Kind: RequestKind.Get,
        ResultType: DisplayItemCollection<OnlineResourceSpaceDisplayItem>
    });
    export namespace StatusOptions {
        export const Get = ApiRoute.Define({
            URL: (attendanceID: string) => `attendance/${attendanceID}/statusoptions`,
            Kind: RequestKind.Get,
            ResultType: AttendanceOptionsDisplayItem
        });

        export const GetByEvent = ApiRoute.Define({
            URL: (eventID: string, attendanceID: string) => `events/${eventID}/attendance/${attendanceID}/statusoptions`,
            Kind: RequestKind.Get,
            ResultType: AttendanceOptionsDisplayItem
        });

        export const GetAll = ApiRoute.Define({
            URL: `summary/attendance/statusoptions`,
            Kind: RequestKind.Post,
            BodyType: AttendanceOptionsSummarySearchOptions,
            ResultType: DisplayItemCollection<AttendanceOptionsSummaryDisplayItem>
        });
    }

    export namespace MakeupCreditIssues {
        export const GetAll = ApiRoute.Define({
            URL: `summary/attendance/makeup`,
            Kind: RequestKind.Post,
            BodyType: AttendanceMakeupCreditIssueSearchOptions,
            ResultType: Array<AttendanceMakeupCreditIssueDisplayItem>
        });
    }

    export const Batch = ApiRoute.Define({
        URL: `attendance`,
        Kind: RequestKind.Patch,
        ResultType: boolean,
        BodyType: BatchAttendanceUpdateItems
    });

    export namespace Update {
        export const ByEvent = ApiRoute.Define({
            URL: (eventID: string, attendanceID: string) => `events/${eventID}/attendance/${attendanceID}`,
            Kind: RequestKind.Put,
            BodyType: AttendanceUpdateItem,
            ResultType: boolean
        });

        export const ByAttendance = ApiRoute.Define({
            URL: (attendanceID: string) => `attendance/${attendanceID}`,
            Kind: RequestKind.Put,
            ResultType: boolean,
            BodyType: AttendanceUpdateItem
        });

        export const GroupNotes = ApiRoute.Define({
            URL: (eventID: string) => `events/${eventID}/groupnote`,
            Kind: RequestKind.Put,
            BodyType: GroupNoteUpdateItem,
            ResultType: boolean
        });
    }

    export const Cancel = ApiRoute.Define({
        URL: (attendanceID: string) => `attendance/${attendanceID}/cancel`,
        Kind: RequestKind.Post,
        BodyType: CancelAttendanceInputItem,
        ResultType: boolean
    });

    export const Reschedule = ApiRoute.Define({
        URL: (attendanceId: string) => `attendance/${attendanceId}/reschedule`,
        Kind: RequestKind.Post,
        BodyType: CalendarBookingInputItem,
        ResultType: CreateAttendanceResultItem
    });

    export const Search = ApiRoute.Define({
        URL: '/search/attendance',
        Kind: RequestKind.Post,
        BodyType: AttendanceSearchOptions,
        PagingAllowed: true,
        ResultType: DisplayItemCollection<AttendanceDisplayItem>
    });

    export namespace EmailNotes {
        export const ForAttendance = ApiRoute.Define({
            URL: (attendanceID: string) => `attendance/${attendanceID}/emailnotes`,
            Kind: RequestKind.Post,
            BodyType: SendAttendanceNotesEmailInputItem,
            ResultType: boolean
        });

        export const ForEvents = ApiRoute.Define({
            URL: (eventID: string, attendanceID: string) => `events/${eventID}/attendance/${attendanceID}/emailnotes`,
            Kind: RequestKind.Post,
            BodyType: SendAttendanceNotesEmailInputItem,
            ResultType: boolean
        });

        export const Group = ApiRoute.Define({
            URL: (eventID: string) => `events/${eventID}/emailnotes`,
            Kind: RequestKind.Post,
            BodyType: SendAttendanceNotesBulkInputItem,
            ResultType: boolean
        });
    }
    export namespace PracticeLogs {
        export const Search = ApiRoute.Define({
            URL: (eventID: string, studentID: string) => `search/event/${eventID}/student/${studentID}/practicelogs`,
            Kind: RequestKind.Post,
            PagingAllowed: true,
            BodyType: PracticeLogSearchOptions,
            ResultType: DisplayItemCollection<PracticeLogDisplayItem>
        });
    }

    export const DetectConflicts = ApiRoute.Define({
        URL: `search/attendance/conflicts`,
        PagingAllowed: true,
        Kind: RequestKind.Post,
        BodyType: AttendanceConflictCheckSearchOptions,
        ResultType: Array<StudentCalendarConflictDisplayItem>
    });

    export const SearchHistory = ApiRoute.Define({
        URL: (eventID: string, attendanceID: string) => `search/events/${eventID}/attendance/${attendanceID}/`,
        Kind: RequestKind.Post,
        BodyType: AttendanceHistorySearchOptions,
        PagingAllowed: true,
        ResultType: DisplayItemCollection<AttendanceDisplayItem>
    });

    export namespace Payment {
        export const Get = ApiRoute.Define({
            URL: (attendanceID: string) => `attendance/${attendanceID}/payment`,
            Kind: RequestKind.Get,
            ResultType: TransactionDisplayItem
        });

        export const Add = ApiRoute.Define({
            URL: (attendanceID: string) => `attendance/${attendanceID}/payment`,
            Kind: RequestKind.Post,
            BodyType: AttendancePaymentInputItem,
            ResultType: string
        });

        export const Delete = ApiRoute.Define({
            URL: (attendanceID: string) => `attendance/${attendanceID}/payment`,
            Kind: RequestKind.Delete,
            ResultType: boolean
        });

        export const Update = ApiRoute.Define({
            URL: (attendanceID: string) => `attendance/${attendanceID}/payment`,
            Kind: RequestKind.Put,
            BodyType: AttendancePaymentUpdateItem,
            ResultType: boolean
        });
    }

    export namespace Attachments {
        export const Get = ApiRoute.Define({
            URL: (attendanceID: string) => `attendance/${attendanceID}/attachments`,
            Kind: RequestKind.Get,
            ResultType: DisplayItemCollection<AttendanceAttachmentDisplayItem>
        });

        export const Add = ApiRoute.Define({
            URL: (attendanceID: string) => `attendance/${attendanceID}/attachments`,
            Kind: RequestKind.Post,
            ResultType: DisplayItemCollection<AttendanceAttachmentDisplayItem>
        });

        export const Update = ApiRoute.Define({
            URL: (attendanceID: string) => `attendance/${attendanceID}/attachments`,
            Kind: RequestKind.Post,
            BodyType: AttendanceAttachmentInputItem,
            ResultType: boolean
        });

        export const Delete = ApiRoute.Define({
            URL: (attendanceID: string, onlineresourceID: string) => `attendance/${attendanceID}/onlineresources/${onlineresourceID}`,
            Kind: RequestKind.Delete,
            ResultType: boolean
        });
    }
}
